/**
 * @fileoverview Client-side Axios service for API requests
 * @active CONFIRMED - This service is actively used in the application
 *
 * This module configures axios instances for API requests.
 * It provides three separate axios instances:
 *  - axiosService: Authenticated requests for protected routes
 *  - axiosPublicService: Public requests for unauthenticated routes
 *  - axiosAuthService: For Supabase authentication endpoints
 *
 * WHEN TO USE:
 * - When you need direct control over HTTP requests
 * - For complex request configurations
 * - When working with non-JSON responses
 * - For authentication-related requests (axiosAuthService)
 * - For public API endpoints (axiosPublicService)
 * - When you need to customize request/response handling
 *
 * NOTE: For most API requests, consider using:
 * 1. aventureApi (preferred for new data fetching with caching)
 * 2. getDataByAxios (for simplified axios requests)
 *
 * The module also defines interceptors for logging requests and responses.
 *
 * @see {@link src/utils/urlUtils.ts} - URL utilities used to determine the base API URL
 * @see {@link src/services/apiService/aventureApi.ts} - Redux Toolkit Query API (preferred for new data fetching)
 * @see {@link src/utils/functionForBack/getDataByAxios.ts} - Simplified axios wrapper
 * @see {@link src/services/apiService/axiosServiceServer.ts} - Server-side Axios service
 * @see {@link docs/backendApiIntegration.md} - Backend API integration details
 */
// src/services/apiService/axiosService.ts

import axios from "axios";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { normalizeApiPath } from "@/utils/urlUtils";

// Safe environment check without any processing or validation
const getApiUrl = () => {
  return process.env.NEXT_PUBLIC_API_URL || "";
};

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Check if we're running in a browser environment
// This is important to distinguish between client-side and server-side code
const isBrowser = typeof window !== 'undefined';

/**
 * Base configuration for authenticated axios instance.
 * Always uses the full API URL.
 *
 * @constant {Object} baseConfigAuthenticated
 * @property {string} baseURL - Base API URL from environment configuration.
 * @property {Object} headers - HTTP headers for requests.
 */
const baseConfigAuthenticated = {
  baseURL: getApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * Base configuration for public axios instance.
 * In development mode on the client-side, baseURL is empty to allow local proxy to work.
 * In all other cases (production or server-side), uses the full API URL.
 *
 * @constant {Object} baseConfigPublic
 * @property {string} baseURL - Base API URL from environment configuration (empty in client-side development).
 * @property {Object} headers - HTTP headers for requests.
 */
const baseConfigPublic = {
  // Only set baseURL to empty in development mode AND in the browser
  // This allows client-side proxy paths like /api-proxy/... to work correctly
  // Server-side code will always use the full API URL
  baseURL: (isDevelopment && isBrowser) ? '' : getApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * Authenticated axios instance for protected routes.
 * This instance includes credentials and is used for routes requiring authentication.
 *
 * @constant {import("axios").AxiosInstance} axiosService
 */
export const axiosService = axios.create({
  ...baseConfigAuthenticated,
  withCredentials: true,
});

/**
 * Public axios instance for unauthenticated routes.
 * It is used for public data fetching, especially in server components.
 * In development mode on the client-side, it has no baseURL to work with the Next.js proxy.
 * IMPORTANT: Different endpoints may return different response structures:
 * 1. Direct data array: { data: T[] }
 * 2. Nested data: { data: { data: T[] } }
 * The interceptors handle both cases automatically.
 *
 * @constant {import("axios").AxiosInstance} axiosPublicService
 */
export const axiosPublicService = axios.create({
  ...baseConfigPublic,
  withCredentials: false,
});

/**
 * Authentication-specific axios instance.
 * Used for Supabase auth endpoints.
 *
 * @constant {import("axios").AxiosInstance} axiosAuthService
 * @see {@link https://app.supabase.com} (External Supabase Auth API)
 */
export const axiosAuthService = axios.create({
  baseURL: (process.env.NEXT_PUBLIC_APP_URL || "").replace(/\/?$/, "/"),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

/**
 * Logs basic details of an outgoing API request.
 *
 * @function logRequestDetails
 * @param {AxiosRequestConfig} config - Axios request configuration.
 */
const logRequestDetails = (config: AxiosRequestConfig) => {
  // Ensure method is a string before calling toUpperCase
  const methodStr =
    typeof config.method === "string"
      ? config.method
      : String(config.method || "GET");
  const method = methodStr.toUpperCase();
  const url = `${config.baseURL || ""}${config.url || ""}`;

  if (process.env.NODE_ENV === "development") {
    console.log(`API Request: ${method} ${url}`);
  }
};

/**
 * Logs basic details of an API response.
 * Handles both nested and direct data structures.
 *
 * @function logResponseDetails
 * @param {AxiosResponse} response - Axios response object.
 */
const logResponseDetails = (response: AxiosResponse) => {
  const config = response.config;
  // Ensure method is a string before calling toUpperCase
  const methodStr =
    typeof config.method === "string"
      ? config.method
      : String(config.method || "GET");
  const method = methodStr.toUpperCase();
  const url = `${config.baseURL || ""}${config.url || ""}`;

  // Handle both nested and direct data structures
  const actualData =
    "data" in response.data ? response.data.data : response.data;
  const dataLength = Array.isArray(actualData) ? actualData.length : "N/A";

  if (process.env.NODE_ENV === "development") {
    console.log(
      `API Response: ${method} ${url} - Status: ${response.status}, Items: ${dataLength}`
    );
  }
};

// Apply interceptors to both authenticated and public services.
[axiosService, axiosPublicService].forEach((instance) => {
  instance.interceptors.request.use(
    (config) => {
      try {
        // Generate request ID for tracing
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

        // Simple path normalization for request URL
        if (config.url) {
          config.url = normalizeApiPath(config.url);
        }

        return config;
      } catch (error) {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
      }
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      logResponseDetails(response);
      return response;
    },
    (error) => {
      // Generate a unique request ID for correlation
      const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      // --- Safely extract hostname ---
      let hostname = "unknown hostname";
      try {
        const fullUrl = error.config?.url?.startsWith("http")
          ? error.config.url
          : `${error.config?.baseURL || ""}${error.config?.url || ""}`;
        if (fullUrl) {
          hostname = new URL(fullUrl).hostname;
        }
      } catch (e) {
        console.warn(`[Axios Interceptor] Failed to parse hostname from URL: ${error.config?.url}, BaseURL: ${error.config?.baseURL}`);
      }
      // --- End hostname extraction ---

      // Extract message with fallbacks
      const message =
        error?.response?.data?.message || error.message || "Unknown API error";

      // Determine error type for better user feedback
      let errorType = "UNKNOWN_ERROR";
      let userFriendlyMessage = "An unexpected error occurred";

      if (!error.response) {
        // Network errors - no response received
        errorType = "NETWORK_ERROR";
        userFriendlyMessage =
          "Network connection issue. Please check your internet connection.";
      } else {
        // Response received with error status
        const status = error.response.status;

        if (status === 401 || status === 403) {
          errorType = "AUTH_ERROR";
          userFriendlyMessage = "Authentication error. Please log in again.";
        } else if (status === 404) {
          errorType = "NOT_FOUND";
          userFriendlyMessage = "The requested resource was not found.";
        } else if (status >= 500) {
          errorType = "SERVER_ERROR";
          userFriendlyMessage = "Server error. Our team has been notified.";
        } else if (status === 429) {
          errorType = "RATE_LIMIT";
          userFriendlyMessage = "Too many requests. Please try again later.";
        } else {
          errorType = "CLIENT_ERROR";
          userFriendlyMessage = "There was an error processing your request.";
        }
      }

      // Create a detailed error info object
      const errorInfo = {
        requestId,
        hostname,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message,
        errorType,
        userFriendlyMessage,
        // Include request details
        request: {
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
          params: error.config?.params,
          data: error.config?.data,
          timeout: error.config?.timeout,
          withCredentials: error.config?.withCredentials,
        },
        // Include response details (if available)
        response: error.response
          ? {
              data: error.response.data,
              headers: error.response.headers,
              status: error.response.status,
              statusText: error.response.statusText,
            }
          : null,
        // Include any potential CORS information
        cors: {
          origin:
            error.config?.headers?.Origin || error.config?.headers?.origin,
          accessControlHeaders:
            error.response?.headers?.["access-control-allow-headers"],
          accessControlOrigin:
            error.response?.headers?.["access-control-allow-origin"],
        },
        // Include raw error details for debugging
        raw: {
          name: error.name,
          code: error.code,
          isAxiosError: error.isAxiosError,
          isNetworkError: error.message?.includes("Network Error"),
        },
        timestamp: new Date().toISOString(),
        // Add flag for automatic retries (could be enabled for network errors)
        canRetry:
          errorType === "NETWORK_ERROR" ||
          (error.response?.status >= 500 && error.response?.status < 600),
      };

      // Enhanced error logging with different levels based on error type
      if (error.response) {
        // Server responded with a status code outside of 2xx range
        console.error(`API Request Failed [${requestId}] to ${hostname}:`, errorInfo);
        // Additional debug log for transient errors (might be race conditions)
        if (error.response.status === 0 || !error.response.data) {
          console.warn(
            `Possible transient error [${requestId}]:`,
            JSON.stringify(error, replacerFunc())
          );
        }

        // Default UI-friendly error message for server errors
        if (error.response.status >= 500) {
          console.warn(
            `⚠️ Server Error [${requestId}]: Consider showing service interruption UI`
          );
        } else if (error.response.status === 404) {
          console.warn(
            `⚠️ Resource Not Found [${requestId}]: Consider showing 404 UI`
          );
        } else if (
          error.response.status === 401 ||
          error.response.status === 403
        ) {
          console.warn(
            `⚠️ Authentication Error [${requestId}]: Consider showing login prompt`
          );
        }
      } else if (error.request) {
        // Request was made but no response received (network issue)
        console.error(`API Network Error [${requestId}] connecting to ${hostname}:`, errorInfo);
        console.warn(
          `⚠️ Network Error [${requestId}]: Consider showing offline/retry UI`
        );
        // Add more detailed logging specifically for development environment
        if (process.env.NODE_ENV === 'development') {
          console.groupCollapsed(
            `[DEV] Detailed Network Error Log [${requestId}]`
          );
          console.log("Request URL:", errorInfo.url);
          console.log("Request Method:", errorInfo.method);
          console.log("Request Headers:", errorInfo.request?.headers);
          console.log("Request Payload:", errorInfo.request?.data);
          console.log(
            "Error Details: The request was sent, but no response was received from the server."
          );
          console.log("Potential CORS Info:", errorInfo.cors);
          console.log("Target Hostname:", hostname);
          console.log("Full Error Info Object:", errorInfo);
          console.groupEnd();
        }
      } else {
        // Error in setting up request
        console.error(`API Request Setup Error [${requestId}] for config targeting ${hostname}:`, errorInfo);
      }

      // Attach the enhanced error info to the error object
      const CUSTOM_ERROR = error as Error & {
        info?: typeof errorInfo;
        userFriendlyMessage?: string;
        errorType?: string;
        canRetry?: boolean;
      };
      CUSTOM_ERROR.info = errorInfo;
      CUSTOM_ERROR.userFriendlyMessage = userFriendlyMessage;
      CUSTOM_ERROR.errorType = errorType;
      CUSTOM_ERROR.canRetry = errorInfo.canRetry;

      return Promise.reject(CUSTOM_ERROR);
    }
  );

  // Helper function to safely stringify circular references in error objects
  function replacerFunc() {
    const visited = new WeakSet();
    return (key: string, value: unknown) => {
      // Skip null and undefined values
      if (value === null || value === undefined) return "[null/undefined]";

      // Handle circular references
      if (typeof value === "object" && value !== null) {
        if (visited.has(value)) {
          return "[Circular]";
        }
        visited.add(value);
      }
      return value;
    };
  }
});

/**
 * Interceptor for axiosAuthService to log request details.
 */
axiosAuthService.interceptors.request.use(
  (config) => {
    logRequestDetails(config);

    // Simple path normalization
    if (config.url) {
      config.url = normalizeApiPath(config.url);
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);
